import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SiteShell from "../components/site/SiteShell";
import { Container, Button, Eyebrow } from "../components/site/ui";
import { Reveal } from "../components/site/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { PACKAGES } from "../data/site";

const empty = { name: "", company: "", email: "", phone: "", business_type: "", needs: "", current_website: "", message: "" };
const PACKAGE_LABEL = Object.fromEntries(PACKAGES.map((p) => [p.id, `${p.name} — ${p.price}`]));

export default function GetMyWebsite() {
  const [params] = useSearchParams();
  const preset = params.get("package");
  const [form, setForm] = useState(empty);
  const [pkg, setPkg] = useState(preset && PACKAGE_LABEL[preset] ? preset : "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder:text-zinc-500 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        company: form.company,
        phone: form.phone,
        business_type: form.business_type,
        current_website: form.current_website,
        service: pkg ? PACKAGE_LABEL[pkg] : "Get My Website",
        message: [form.needs, form.message].filter(Boolean).join("\n\n") || "New project request.",
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteShell title="Get My Website — GoBuildWeb" description="Start your project in under two minutes. Tell us about your business and we'll build the website your business can grow on." testId="get-website-page">
      <section className="pt-[72px]">
        <Container className="grid items-start gap-12 py-16 md:py-24 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <Eyebrow>Get started</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white md:text-5xl">Let's build your website</h1>
            <p className="mt-4 max-w-md text-zinc-400">Tell us a little about your business. It takes less than two minutes — no tech knowledge needed. We'll follow up within one business day.</p>
            <ul className="mt-8 space-y-3">
              {["Human-built, custom design", "Starting at $999", "We handle hosting, security & launch"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-zinc-300"><CheckCircle2 size={16} className="text-gold-500" /> {t}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
              {done ? (
                <div className="py-10 text-center" data-testid="get-website-success">
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gold-500/15 text-gold-400"><CheckCircle2 size={32} /></span>
                  <h2 className="mt-5 font-display text-3xl font-bold text-white">You're all set!</h2>
                  <p className="mx-auto mt-3 max-w-sm text-zinc-400">Thanks — we got your request and our team will reach out within one business day to start building.</p>
                  <div className="mt-7 flex justify-center gap-3">
                    <Button to="/what-we-can-build" variant="secondary" icon={false}>See what we can build</Button>
                    <Button to="/">Back home</Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="get-website-form">
                  <input className={input} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="gw-name" />
                  <input className={input} placeholder="Business name" value={form.company} onChange={set("company")} data-testid="gw-business" />
                  <input type="email" className={input} placeholder="Email *" value={form.email} onChange={set("email")} data-testid="gw-email" />
                  <input className={input} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="gw-phone" />
                  <input className={input} placeholder="Business type (e.g. auto, restaurant)" value={form.business_type} onChange={set("business_type")} data-testid="gw-type" />
                  <select className={input} value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="gw-package">
                    <option value="">Which package? (optional)</option>
                    {PACKAGES.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.price}</option>)}
                  </select>
                  <input className={`${input} sm:col-span-2`} placeholder="Current website (if any)" value={form.current_website} onChange={set("current_website")} data-testid="gw-current" />
                  <textarea rows={3} className={`${input} sm:col-span-2 resize-none`} placeholder="What do you need? *" value={form.needs} onChange={set("needs")} data-testid="gw-needs" />
                  <textarea rows={2} className={`${input} sm:col-span-2 resize-none`} placeholder="Anything else? (optional)" value={form.message} onChange={set("message")} data-testid="gw-message" />
                  <button type="submit" disabled={loading} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 px-6 py-3.5 font-semibold text-black transition hover:bg-gold-400 disabled:opacity-60" data-testid="gw-submit">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                    {loading ? "Sending…" : "Start My Project"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
    </SiteShell>
  );
}
